// client/src/pages/timeTracking/TimeTrackingAnalytics.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Collapse,
  AvatarGroup,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Timer as TimerIcon,
  People as PeopleIcon,
  Assignment as TaskIcon,
  TrendingUp as TrendingIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  AccessTime as ClockIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { debounce } from 'lodash';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Import existing components
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import EntityTableView from '../../components/ui/EntityTableView';
import PageHeader from '../../components/ui/PageHeader';
import useAuthStore from '../../stores/authStore';
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';
import useUserStore from '../../stores/userStore';
import useProjectStore from '../../stores/projectStore';
import useClientStore from '../../stores/clientStore';
import { formatDuration, formatDate } from '../../utils/dateUtils';
import useTaskStore from '../../stores/taskStore';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// Component to group entries by task with MinimalTaskDetails-like UI
const GroupedTaskEntries = ({ entries }) => {
  const [expandedTasks, setExpandedTasks] = useState({});
  const theme = useTheme();
  const { openTaskDrawer } = useTaskStore();

  // Group entries by taskId
  const groupedEntries = useMemo(() => {
    const groups = {};
    entries.forEach(entry => {
      const taskKey = entry.taskId || 'no-task';
      if (!groups[taskKey]) {
        groups[taskKey] = {
          taskId: entry.taskId,
          taskName: entry.taskName || 'Untitled Task',
          project: entry.project,
          entries: [],
          totalHours: 0,
          users: new Set()
        };
      }
      groups[taskKey].entries.push(entry);
      groups[taskKey].totalHours += entry.hours || 0;
      if (entry.user) {
        groups[taskKey].users.add(JSON.stringify(entry.user));
      }
    });

    // Convert users Set back to array of unique users
    Object.values(groups).forEach(group => {
      group.users = Array.from(group.users).map(userStr => JSON.parse(userStr));
    });

    return Object.values(groups);
  }, [entries]);

  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  return (
    <Stack spacing={2}>
      {groupedEntries.map((group) => (
        <Paper
          key={group.taskId || 'no-task'}
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: theme.shadows[2],
              borderColor: theme.palette.primary.main,
            }
          }}
        >
          {/* Task Header */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: expandedTasks[group.taskId] ? 2 : 0
            }}
            onClick={() => toggleExpand(group.taskId)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <IconButton size="small">
                {expandedTasks[group.taskId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight="medium"
                  sx={{ 
                    cursor: group.taskId && group.taskId !== 'no-task' ? 'pointer' : 'default',
                    '&:hover': group.taskId && group.taskId !== 'no-task' ? { 
                      textDecoration: 'underline',
                      color: 'primary.main'
                    } : {}
                  }}
                  onClick={(e) => {
                    if (group.taskId && group.taskId !== 'no-task') {
                      e.stopPropagation();
                      openTaskDrawer(group.taskId);
                    }
                  }}
                >
                  {group.taskName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {group.project && (
                    <>
                      <Chip
                        label={group.project.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 24 }}
                      />
                      {group.project.status && (
                        <Chip
                          label={group.project.status}
                          size="small"
                          color={group.project.status === 'active' ? 'success' : 'default'}
                          sx={{ height: 24 }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Task Summary Stats */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {formatHours(group.totalHours)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Time
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">
                  {group.entries.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Entries
                </Typography>
              </Box>
              
              <AvatarGroup max={3} sx={{ ml: 1 }}>
                {group.users.map((user, idx) => (
                  <Tooltip key={idx} title={user.name || user.email || 'Unknown'}>
                    <Avatar sx={{ width: 32, height: 32 }} src={user.avatar}>
                      {(user.name || user.email || 'U').charAt(0)}
                    </Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            </Box>
          </Box>

          {/* Expandable Time Entries */}
          <Collapse in={expandedTasks[group.taskId]} timeout="auto" unmountOnExit>
            <Box sx={{ 
              mt: 2, 
              pt: 2, 
              borderTop: `1px solid ${theme.palette.divider}` 
            }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Time Entry Details
              </Typography>
              
              <Stack spacing={1}>
                {group.entries.map((entry, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    {/* Entry User Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Avatar sx={{ width: 36, height: 36 }} src={entry.user?.avatar}>
                        {(entry.user?.name || entry.user?.email || 'U').charAt(0)}
                      </Avatar>
                      
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {entry.user?.name || entry.user?.email || 'Unknown'}
                          </Typography>
                          <Chip
                            label={entry.isManual ? 'Manual' : 'Tracked'}
                            size="small"
                            variant="outlined"
                            color={entry.isManual ? 'default' : 'primary'}
                            sx={{ height: 20 }}
                          />
                        </Box>
                        {entry.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {entry.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Entry Time Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="medium">
                          <ClockIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          {formatHours(entry.hours || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.startTime ? new Date(entry.startTime).toLocaleDateString() : '-'}
                        </Typography>
                      </Box>
                      
                      {entry.startTime && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          {entry.endTime && (
                            <>
                              <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                                -
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(entry.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Paper>
      ))}
    </Stack>
  );
};

const TimeTrackingAnalytics = () => {
  const { user } = useAuthStore();
  const { 
    timeTrackingAnalytics: data,
    isLoading: loading,
    isExporting,
    error,
    fetchTimeTrackingAnalytics,
    exportTimeTrackingAnalytics,
    clearError
  } = useAnalyticsStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Fetch filter options
  const { users = [], fetchUsers } = useUserStore();
  const { projects = [], fetchProjects } = useProjectStore();
  const { clients = [], fetchClients } = useClientStore();
  
  const [activeTab, setActiveTab] = useState(0);
  
  // Set default date range to last month
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return { startDate, endDate };
  };
  
  // Get date range from period preset
  const getDateRangeFromPeriod = (period) => {
    const endDate = new Date();
    const startDate = new Date();
    
    switch (period) {
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      case 'ytd':
        startDate.setMonth(0);
        startDate.setDate(1);
        break;
      default:
        startDate.setMonth(startDate.getMonth() - 1);
    }
    
    return { startDate, endDate };
  };
  
  const defaultDates = getDefaultDateRange();
  const [filters, setFilters] = useState({
    period: 'month',
    startDate: defaultDates.startDate.toISOString().split('T')[0],
    endDate: defaultDates.endDate.toISOString().split('T')[0],
    userId: null,
    projectId: null,
    clientId: null
  });

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    try {
      await fetchTimeTrackingAnalytics(filters);
    } catch (err) {
      console.error('Error fetching time tracking analytics:', err);
    }
  }, [filters, fetchTimeTrackingAnalytics]);

  // Create debounced fetch function
  const debouncedFetch = useMemo(
    () => debounce(() => {
      fetchAnalytics();
    }, 500),
    [fetchAnalytics]
  );

  // Fetch filter options on mount
  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchClients();
    fetchAnalytics(); // Initial fetch
  }, []); // Only on mount
  
  // Fetch when filters change (debounced)
  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [filters.startDate, filters.endDate, filters.userId, filters.projectId, filters.clientId, debouncedFetch]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleExport = async () => {
    try {
      await exportTimeTrackingAnalytics(filters);
      showSuccess('Export completed successfully!');
    } catch (err) {
      console.error('Export failed:', err);
      showError('Failed to export analytics data');
    }
  };

  const formatHours = (hours) => {
    return `${hours.toFixed(1)}h`;
  };

  // Summary Cards
  const SummaryCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.light`, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">No data available</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <PageHeader
        title="Time Tracking Analytics"
        subtitle="Monitor and analyze time tracking across all projects and users"
        actions={[
          <Button
            key="refresh"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAnalytics}
          >
            Refresh
          </Button>,
          <Button
            key="export"
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
          >
            Export
          </Button>
        ]}
      />

      {/* Inline Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Picker */}
          <Grid item xs={12} md={3}>
            <DateRangePicker
              startDate={filters.startDate ? new Date(filters.startDate) : null}
              endDate={filters.endDate ? new Date(filters.endDate) : null}
              onChange={(startDate, endDate) => {
                handleFilterChange({
                  startDate: startDate ? new Date(startDate).toISOString().split('T')[0] : null,
                  endDate: endDate ? new Date(endDate).toISOString().split('T')[0] : null,
                  period: null
                });
              }}
              presetPeriod={filters.period}
              onPresetChange={(period) => {
                const dates = getDateRangeFromPeriod(period);
                handleFilterChange({ 
                  period,
                  startDate: dates.startDate.toISOString().split('T')[0],
                  endDate: dates.endDate.toISOString().split('T')[0]
                });
              }}
            />
          </Grid>
          
          {/* User Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={filters.userId || ''}
                label="User"
                onChange={(e) => handleFilterChange({ userId: e.target.value || null })}
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Project Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Project</InputLabel>
              <Select
                value={filters.projectId || ''}
                label="Project"
                onChange={(e) => handleFilterChange({ projectId: e.target.value || null })}
              >
                <MenuItem value="">All Projects</MenuItem>
                {projects.map(project => (
                  <MenuItem key={project._id} value={project._id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Client Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select
                value={filters.clientId || ''}
                label="Client"
                onChange={(e) => handleFilterChange({ clientId: e.target.value || null })}
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients.map(client => (
                  <MenuItem key={client._id} value={client._id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Active Filters Display & Clear Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Show active filter chips */}
              {filters.userId && (
                <Chip
                  size="small"
                  label={users.find(u => u._id === filters.userId)?.name || 'User'}
                  onDelete={() => handleFilterChange({ userId: null })}
                />
              )}
              {filters.projectId && (
                <Chip
                  size="small"
                  label={projects.find(p => p._id === filters.projectId)?.name || 'Project'}
                  onDelete={() => handleFilterChange({ projectId: null })}
                />
              )}
              {filters.clientId && (
                <Chip
                  size="small"
                  label={clients.find(c => c._id === filters.clientId)?.name || 'Client'}
                  onDelete={() => handleFilterChange({ clientId: null })}
                />
              )}
              
              {/* Clear All Filters Button */}
              {(filters.userId || filters.projectId || filters.clientId) && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    handleFilterChange({
                      userId: null,
                      projectId: null,
                      clientId: null
                    });
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Hours"
            value={formatHours(data?.summary?.totalHours || 0)}
            icon={<TimerIcon />}
            color="primary"
            subtitle={`${data?.summary?.totalEntries || 0} entries`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active Users"
            value={data?.summary?.uniqueUsers || 0}
            icon={<PeopleIcon />}
            color="success"
            subtitle={`${formatHours(data?.summary?.avgHoursPerUser || 0)} avg/user`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Tasks Tracked"
            value={data?.summary?.uniqueTasks || 0}
            icon={<TaskIcon />}
            color="warning"
            subtitle="Unique tasks"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Daily Average"
            value={formatHours(data?.summary?.avgHoursPerDay || 0)}
            icon={<TrendingIcon />}
            color="info"
            subtitle="Hours per day"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab label="By User" />
          <Tab label="By Project" />
          <Tab label="Recent Entries" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Time Trend Chart */}
          <Grid item xs={12}>
            <ChartCard
              title="Hours Tracked Over Time"
              subtitle="Daily time tracking trend"
              actions={[
                { icon: <RefreshIcon />, onClick: fetchAnalytics }
              ]}
            >
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.timeTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="hours" 
                    stroke="#8884d8" 
                    name="Hours"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="userCount" 
                    stroke="#82ca9d" 
                    name="Active Users"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          {/* User Distribution */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Hours by User"
              subtitle="Top contributors"
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={(data?.hoursByUser?.slice(0, 10) || []).map(item => ({
                  ...item,
                  userName: item.user?.name || item.user?.email || 'Unknown'
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="userName" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="totalHours" fill="#8884d8" name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          {/* Project Distribution */}
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Hours by Project"
              subtitle="Project time allocation"
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data?.hoursByProject?.slice(0, 6) || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.project?.name || 'Unknown'}: ${formatHours(entry.totalHours)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="totalHours"
                  >
                    {(data?.hoursByProject?.slice(0, 6) || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          {data?.hoursByUser && data.hoursByUser.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Hours by User ({data.hoursByUser.length} users)
              </Typography>
              <EntityTableView
                items={data.hoursByUser}
            columns={[
              {
                id: 'user',
                label: 'User',
                renderCell: (row) => (
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 2 }} src={row.user?.avatar}>
                      {row.user?.name?.charAt(0) || row.user?.email?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{row.user?.name || 'Unknown User'}</Typography>
                      {row.user?.email && (
                        <Typography variant="caption" color="textSecondary">
                          {row.user.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              },
              {
                id: 'totalHours',
                label: 'Total Hours',
                renderCell: (row) => formatHours(row.totalHours)
              },
              {
                id: 'entryCount',
                label: 'Entries',
                renderCell: (row) => row.entryCount
              },
              {
                id: 'taskCount',
                label: 'Tasks',
                renderCell: (row) => row.taskCount
              },
              {
                id: 'avgHoursPerTask',
                label: 'Avg Hours/Task',
                renderCell: (row) => formatHours(row.avgHoursPerTask)
              }
            ]}
              />
            </>
          ) : (
            <Alert severity="warning">
              No user data available. Data object: {JSON.stringify(data?.hoursByUser)}
            </Alert>
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 2 }}>
          <EntityTableView
            items={data?.hoursByProject || []}
            columns={[
              {
                id: 'project',
                label: 'Project',
                renderCell: (row) => (
                  <Box>
                    <Typography variant="body2">{row.project.name}</Typography>
                    <Chip 
                      label={row.project.status} 
                      size="small" 
                      color={row.project.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                )
              },
              {
                id: 'totalHours',
                label: 'Total Hours',
                renderCell: (row) => formatHours(row.totalHours)
              },
              {
                id: 'taskCount',
                label: 'Tasks',
                renderCell: (row) => row.taskCount
              },
              {
                id: 'userCount',
                label: 'Team Size',
                renderCell: (row) => row.userCount
              },
              {
                id: 'avgHoursPerUser',
                label: 'Avg Hours/User',
                renderCell: (row) => formatHours(row.avgHoursPerUser)
              },
              {
                id: 'entryCount',
                label: 'Total Entries',
                renderCell: (row) => row.entryCount
              }
            ]}
          />
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 2 }}>
          {data?.recentEntries && data.recentEntries.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recent Time Entries ({data.recentEntries.length} entries)
              </Typography>
              <GroupedTaskEntries entries={data.recentEntries} />
            </>
          ) : (
            <Alert severity="info">
              No recent time entries found for the selected date range.
            </Alert>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default TimeTrackingAnalytics;